import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { useRef, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { Skeleton } from '../feedback/Skeleton';
import { ErrorState } from '../feedback/ErrorState';
import { useIsCompact } from '../layout/useMediaQuery';
import { focusRing } from '../primitives/_shared';

type Align = 'start' | 'center' | 'end';
const textAlign: Record<Align, string> = { start: 'left', center: 'center', end: 'right' };
const justify: Record<Align, string> = { start: 'flex-start', center: 'center', end: 'flex-end' };

export type SortDirection = 'asc' | 'desc';

export interface Column<T> {
  /** Stable id — also the sort key reported to `onSortChange`. */
  id: string;
  header: ReactNode;
  /** Cell renderer for one row. */
  cell: (row: T) => ReactNode;
  align?: Align;
  /** CSS width / min-width hint for the column (e.g. '12rem', 'minmax(0,1fr)'). */
  width?: string;
  /**
   * CSS max-width for the column's cells. When set, the cell clips overflow so its
   * `truncate` content can ellipsize (e.g. `maxWidth: '0'` on a fluid column makes it
   * fill the leftover width and truncate). Additive — columns that omit it are unchanged.
   */
  maxWidth?: string;
  sortable?: boolean;
  /** Short label used before the value in the responsive card layout. */
  cardLabel?: ReactNode;
  /** Hide this column's value-label in card mode (e.g. status, actions). */
  hideCardLabel?: boolean;
  /** Omit this column entirely from the responsive card layout. */
  hideOnCard?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /** Row currently selected/active (master-detail highlight). */
  activeRowId?: string;
  /** Row activation — click, Enter, or Space. Makes rows focusable + clickable. */
  onRowClick?: (row: T) => void;
  /** Accessible caption for the table (visually hidden). */
  caption: string;
  loading?: boolean;
  /** True (or a node) renders the error surface; `onRetry` wires the retry button. */
  error?: boolean | ReactNode;
  onRetry?: () => void;
  /** Whether the loaded data is empty; renders `emptyState` when true. */
  isEmpty?: boolean;
  emptyState?: ReactNode;
  /** Sort state + handler (keyset/server or client — the table only reports intent). */
  sort?: { columnId: string; direction: SortDirection };
  onSortChange?: (columnId: string) => void;
  /** Testid stamped on each row (list `<tr>` and each responsive card). */
  rowTestId?: string;
  /** Trailing actions cell (delete, etc.); rendered outside the row's activation hit-area. */
  rowActions?: (row: T) => ReactNode;
  /** Footer slot — e.g. a keyset "Load more" control or pagination. */
  footer?: ReactNode;
  /** Skeleton row count while loading. */
  loadingRows?: number;
  /**
   * Presentation: `auto` (default) uses the table on `md`+ and cards below; `table`
   * and `cards` force one form. `cards` is useful inside a narrow master pane (a
   * split view) where a multi-column table would overflow.
   */
  layout?: 'auto' | 'table' | 'cards';
}

const Scroll = chakra('div', { base: { minWidth: 0, width: 'full' } });

const Table = chakra('table', {
  base: {
    width: 'full',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontFamily: 'body',
    fontSize: 'md',
  },
});

const Th = chakra('th', {
  base: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    bg: 'surface.default',
    color: 'text.muted',
    fontSize: 'xs',
    fontWeight: 'semibold',
    letterSpacing: 'wide',
    textTransform: 'uppercase',
    textAlign: 'left',
    px: '3',
    py: '2.5',
    borderBottomWidth: '1px',
    borderColor: 'border.default',
    whiteSpace: 'nowrap',
  },
});

const SortButton = chakra('button', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1',
    font: 'inherit',
    letterSpacing: 'inherit',
    textTransform: 'inherit',
    color: 'inherit',
    bg: 'transparent',
    border: 'none',
    cursor: 'pointer',
    p: 0,
    _hover: { color: 'text.secondary' },
    ...focusRing,
  },
});

const Tr = chakra('tr', {
  base: {
    transitionProperty: 'background-color',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    '&[data-clickable="true"]': { cursor: 'pointer' },
    '&[data-clickable="true"]:hover': { bg: 'surface.subtle' },
    '&[aria-selected="true"]': { bg: 'action.primary/8' },
    ...focusRing,
    _focusVisible: { outlineOffset: '-2px', ...focusRing._focusVisible },
  },
});

const Td = chakra('td', {
  base: {
    px: '3',
    py: '3',
    borderBottomWidth: '1px',
    borderColor: 'border.subtle',
    color: 'text.primary',
    verticalAlign: 'middle',
    minWidth: 0,
  },
});

/** Card list used below the `md` breakpoint — no horizontal scroll. */
const Card = chakra('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2',
    p: '4',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'lg',
    bg: 'surface.default',
    transitionProperty: 'background-color, border-color',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    '&[data-clickable="true"]': { cursor: 'pointer' },
    '&[data-clickable="true"]:hover': { borderColor: 'border.strong', bg: 'surface.subtle' },
    '&[aria-selected="true"]': { borderColor: 'action.primary', bg: 'action.primary/8' },
    ...focusRing,
  },
});

const CardRow = chakra('div', {
  base: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '3', minWidth: 0 },
});

const CardLabel = chakra('span', {
  base: { fontSize: 'xs', fontWeight: 'medium', color: 'text.muted', flexShrink: 0 },
});

const CardValue = chakra('div', { base: { minWidth: 0, textAlign: 'right' } });

const Stack = chakra('div', { base: { display: 'flex', flexDirection: 'column', gap: '3' } });

function useRowKeyboardNav(onRowClick?: <T>(row: T) => void) {
  const containerRef = useRef<HTMLElement>(null);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!onRowClick) return;
    const focusables = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>('[data-row="true"]') ?? [],
    );
    const idx = focusables.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusables[Math.min(idx + 1, focusables.length - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusables[Math.max(idx - 1, 0)]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusables[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      focusables[focusables.length - 1]?.focus();
    }
  };
  return { containerRef, onKeyDown };
}

/**
 * The app's accessible data-table pattern — column defs, an active/selected row,
 * keyboard row navigation (↑/↓/Home/End + Enter/Space to activate), a sticky header,
 * and loading / empty / error states baked in. Below `md` it reflows to a stacked card
 * list (never a horizontal scroll). Sorting and pagination are hook-ins: the table
 * reports intent (`onSortChange`) and renders a `footer` slot for keyset "load more".
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  activeRowId,
  onRowClick,
  caption,
  loading,
  error,
  onRetry,
  isEmpty,
  emptyState,
  sort,
  onSortChange,
  rowTestId,
  rowActions,
  footer,
  loadingRows = 5,
  layout = 'auto',
}: DataTableProps<T>) {
  const viewportCompact = useIsCompact();
  const compact = layout === 'cards' || (layout === 'auto' && viewportCompact);
  const { containerRef, onKeyDown } = useRowKeyboardNav(onRowClick as never);

  if (error) {
    return typeof error === 'boolean' ? (
      <ErrorState onRetry={onRetry} description="This list could not be loaded." />
    ) : (
      <>{error}</>
    );
  }

  if (loading) {
    return compact ? (
      <Stack aria-busy data-testid="datatable-loading">
        {Array.from({ length: loadingRows }).map((_, i) => (
          <Skeleton key={i} variant="rect" height="5rem" />
        ))}
      </Stack>
    ) : (
      <Scroll aria-busy data-testid="datatable-loading">
        <Table>
          <TableHead columns={columns} sort={sort} onSortChange={onSortChange} hasActions={!!rowActions} />
          <tbody>
            {Array.from({ length: loadingRows }).map((_, i) => (
              <chakra.tr key={i}>
                {columns.map((c) => (
                  <Td key={c.id}>
                    <Skeleton variant="text" width={c.align === 'end' ? '40%' : '70%'} />
                  </Td>
                ))}
                {rowActions && <Td />}
              </chakra.tr>
            ))}
          </tbody>
        </Table>
      </Scroll>
    );
  }

  if (isEmpty) return <>{emptyState}</>;

  const clickable = !!onRowClick;

  const activate = (row: T) => onRowClick?.(row);
  const onRowKey = (e: React.KeyboardEvent, row: T) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate(row);
    }
  };

  // ---- Responsive card layout (below md) --------------------------------------
  if (compact) {
    return (
      <>
        <Stack ref={containerRef as React.Ref<HTMLDivElement>} role="list" onKeyDown={onKeyDown}>
          {rows.map((row) => {
            const id = getRowId(row);
            const selected = id === activeRowId;
            return (
              <Card
                key={id}
                role="listitem"
                data-testid={rowTestId}
                data-row={clickable ? 'true' : undefined}
                data-clickable={clickable || undefined}
                aria-selected={clickable ? selected : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => activate(row) : undefined}
                onKeyDown={clickable ? (e) => onRowKey(e, row) : undefined}
              >
                {columns
                  .filter((c) => !c.hideOnCard)
                  .map((c) => (
                    <CardRow key={c.id}>
                      {!c.hideCardLabel && <CardLabel>{c.cardLabel ?? c.header}</CardLabel>}
                      <CardValue>{c.cell(row)}</CardValue>
                    </CardRow>
                  ))}
                {rowActions && <chakra.div alignSelf="flex-end">{rowActions(row)}</chakra.div>}
              </Card>
            );
          })}
        </Stack>
        {footer && <chakra.div mt="4">{footer}</chakra.div>}
      </>
    );
  }

  // ---- Table layout (md and up) -----------------------------------------------
  return (
    <>
      <Scroll ref={containerRef as React.Ref<HTMLDivElement>} onKeyDown={onKeyDown}>
        <Table>
          <chakra.caption srOnly>{caption}</chakra.caption>
          <TableHead columns={columns} sort={sort} onSortChange={onSortChange} hasActions={!!rowActions} />
          <tbody>
            {rows.map((row) => {
              const id = getRowId(row);
              const selected = id === activeRowId;
              return (
                <Tr
                  key={id}
                  data-testid={rowTestId}
                  data-row={clickable ? 'true' : undefined}
                  data-clickable={clickable || undefined}
                  aria-selected={clickable ? selected : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => activate(row) : undefined}
                  onKeyDown={clickable ? (e) => onRowKey(e, row) : undefined}
                >
                  {columns.map((c) => {
                    const cellStyle: HTMLChakraProps<'td'> = {
                      textAlign: c.align ? (textAlign[c.align] as HTMLChakraProps<'td'>['textAlign']) : undefined,
                      width: c.width,
                      maxWidth: c.maxWidth,
                      overflow: c.maxWidth != null ? 'hidden' : undefined,
                    };
                    return (
                      <Td key={c.id} {...cellStyle}>
                        {c.cell(row)}
                      </Td>
                    );
                  })}
                  {rowActions && (
                    <Td textAlign="right" width="1%" onClick={(e) => e.stopPropagation()}>
                      {rowActions(row)}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </Scroll>
      {footer && <chakra.div mt="4">{footer}</chakra.div>}
    </>
  );
}

function TableHead<T>({
  columns,
  sort,
  onSortChange,
  hasActions,
}: {
  columns: Column<T>[];
  sort?: { columnId: string; direction: SortDirection };
  onSortChange?: (columnId: string) => void;
  hasActions: boolean;
}) {
  return (
    <thead>
      <tr>
        {columns.map((c) => {
          const active = sort?.columnId === c.id;
          const headStyle: HTMLChakraProps<'th'> = {
            textAlign: c.align ? (textAlign[c.align] as HTMLChakraProps<'th'>['textAlign']) : undefined,
            width: c.width,
            maxWidth: c.maxWidth,
          };
          return (
            <Th key={c.id} scope="col" aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined} {...headStyle}>
              {c.sortable && onSortChange ? (
                <SortButton
                  type="button"
                  onClick={() => onSortChange(c.id)}
                  style={{ justifyContent: c.align ? justify[c.align] : undefined }}
                >
                  {c.header}
                  {active ? (
                    sort!.direction === 'asc' ? (
                      <ChevronUp size={13} strokeWidth={2.5} aria-hidden />
                    ) : (
                      <ChevronDown size={13} strokeWidth={2.5} aria-hidden />
                    )
                  ) : (
                    <ChevronsUpDown size={13} strokeWidth={2} aria-hidden style={{ opacity: 0.5 }} />
                  )}
                </SortButton>
              ) : (
                c.header
              )}
            </Th>
          );
        })}
        {hasActions && (
          <Th scope="col" textAlign="right">
            <chakra.span srOnly>Actions</chakra.span>
          </Th>
        )}
      </tr>
    </thead>
  );
}
